import { size, countBy, sum } from 'lodash-es';
import type { CoreTypes } from '../../core';
import type { Types as PlanDigitalTypes } from '../types';
import type { InternalIdKey } from '../../core/types/common';

namespace OutTypes {
  export type group   = Omit<CoreTypes.Serialized.Group,   InternalIdKey>;
  export type teacher = Omit<CoreTypes.Serialized.Teacher, InternalIdKey>;
  export type course  = Omit<CoreTypes.Serialized.Course,  InternalIdKey>;
}

type Out = {
  groups?:   OutTypes.group[];
  teachers?: OutTypes.teacher[];
  courses?:  OutTypes.course[];
  meta?: {
    errors?:   string[];
    warnings?: string[];
  };
};

export default (
  input:   PlanDigitalTypes.schedule,
  options: PlanDigitalTypes.options
): Out => {
  // gather all import related warnings/errors
  const warnings: string[] = [];
  const errors:   string[] = [];

  const periods         = input['Perioder'] ?? [];
  const selectedPeriods = options.selectedPeriods ?? [];

  // ensure that the selected periods exist
  for (const x of selectedPeriods) {
    if (!periods.some(p => p.Namn == x)) {
      errors.push(`The selected period ${x} does not exist.`);
      return { meta: { errors } };
    }
  }

  // ensure that all periods use the same time unit
  const numUnits = size(countBy(periods.map(x => x.Tidsenhet)));
  if (numUnits > 1) {
    errors.push('Different time periods used for different periods.');
    return { meta: { errors } };
  }
  const timeUnit = periods[0]?.Tidsenhet;


  ////
  //// fetch courses, groups, and teachers
  ////
  const courses    = new Array<OutTypes.course>();
  const teacherSet = new Set<string>();
  const groupSet   = new Set<string>();
  input['Tabell Tjänstefördelning'].forEach(c => {
    // check if the course should be taken into account
    // (when periods are selected, the course must have some scheduled time during them)
    const periodTimes = selectedPeriods.map(x => c[`Tid ${x}`] ?? 0);
    if (selectedPeriods.length && sum(periodTimes) == 0) return;

    const displayName = c[options.displayName ?? 'Gruppnamn'];
    const subject     = c['Kurskod'];
    const groups      = String(c['Klass'] ?? '').split(',').map(x => x.trim()).filter(Boolean);
    const teachers    = String(c['Sign']  ?? '').split(',').map(x => x.trim()).filter(Boolean);

    // fetch the expected schedule time in whatever units
    const periodTime         = periodTimes.length && size(countBy(periodTimes)) == 1 ? periodTimes[0] : undefined;
    const minutesPerWeek     = timeUnit == 'Minuter/Vecka'         ? periodTime     : undefined;
    const expectedTotalHours = timeUnit == 'Timmar/Period'         ? periodTime     : undefined;

    // estimate total minutes planned across the selected periods
    const minutesPlanned = selectedPeriods.reduce((total, x) => {
      const time  = c[`Tid ${x}`] ?? 0;
      const weeks = (c as unknown as Record<string, number | undefined>)[`Veckor ${x}`] ?? 0;

      if (timeUnit == 'Minuter/Vecka') return total + time * weeks;
      if (timeUnit == 'Timmar/Period') return total + time * 60;

      return total;
    }, 0) || undefined;

    // add groups and teachers
    teachers.forEach(x => teacherSet.add(x));
    groups.forEach(x   => groupSet.add(x));

    // add course
    courses.push({
      displayName,
      subject,
      teachers: teachers.map(to => ({ to })),
      groups:   groups  .map(to => ({ to })),

      ...(minutesPerWeek     != undefined && { minutesPerWeek     }),
      ...(expectedTotalHours != undefined && { expectedTotalHours }),
      ...(minutesPlanned     != undefined && { minutesPlanned     })
    });
  });

  const teachers = [...teacherSet].map(signature => ({ signature, displayName: signature }));
  const groups   = [...groupSet  ].map(ids => ({ ids, displayName: ids, species: 'class' as const }));
  return { groups, teachers, courses,
    meta: {
      ...( errors.length   && { errors:   errors   } ),
      ...( warnings.length && { warnings: warnings } )
    }
  };
};